import React, { useEffect, useState } from "react";
import {
  LayoutDashboard, Truck, CreditCard, Package, RotateCcw, MessageCircle,
  BarChart3, ShieldCheck, Zap, FileText, Check, ArrowRight, PhoneCall,
  X, Store, Boxes, Quote,
} from "lucide-react";

// BoxHero.com brand system: blue #406CB4, orange #F9AA25, Poppins headings.
const LOGO_COLOR = "https://customer-assets.emergentagent.com/job_client-hub-266/artifacts/ysfg7n2y_Asset%209blue.png";

const BOOK_A_CALL_URL = "https://calendly.com/getboxhero/3pl";

const F = "Poppins, sans-serif";

const FEATURES = [
  { icon: LayoutDashboard, title: "Live Business Dashboard", desc: "Orders, shipments, units and carrier mix — always current. Your brands never have to email for a status update again." },
  { icon: Truck, title: "Real-Time Order Tracking", desc: "Every shipment with live carrier status and one-click tracking, plus estimated delivery dates." },
  { icon: CreditCard, title: "Invoices & Billing Portal", desc: "Downloadable PDF invoices, one-click Pay Now and a secure Stripe portal to manage payment methods." },
  { icon: Package, title: "Inventory Visibility", desc: "True on-hand stock by SKU and full visibility into inbound POs arriving at the warehouse." },
  { icon: RotateCcw, title: "Returns & Claims", desc: "See returns with photos and disposition, and file damage or loss claims with a clear status timeline." },
  { icon: BarChart3, title: "Reports & Exports", desc: "Self-serve CSV and XLSX exports of orders, billing and fulfillment data — anytime, no waiting." },
  { icon: MessageCircle, title: "WhatsApp-First Support", desc: "Each brand gets their own support group link right inside the app. Fast, human help — no tickets." },
  { icon: FileText, title: "Documents & Contracts", desc: "Agreements, rate cards and paperwork, shared securely and always at your brand's fingertips." },
];

const SHOWCASE = [
  { src: "/gallery/10_dashboard.png", label: "Client Dashboard", desc: "Live fulfillment analytics for every brand" },
  { src: "/gallery/11_orders_shipments.png", label: "Orders & Shipments", desc: "Status and tracking at a glance" },
  { src: "/gallery/14_invoices.png", label: "Invoices & Billing", desc: "Automated, itemized, Stripe-powered" },
  { src: "/gallery/17_inventory.png", label: "Inventory & Stock", desc: "True on-hand counts by SKU" },
  { src: "/gallery/40_receiving_returns.png", label: "Returns & Dispositions", desc: "Item photos, condition tags and outcomes" },
  { src: "/gallery/30_cockpit.png", label: "Operations Cockpit", desc: "A pulse across the whole customer base" },
];

const TESTIMONIALS = [
  { quote: "The dashboard is the reason we switched. For the first time we can see exactly what's in stock, what shipped and what's coming back — without emailing anyone.", who: "Founder", tag: "DTC apparel brand" },
  { quote: "Our old 3PL sent spreadsheets once a week. BoxHero gives our whole team a live view they can pull up on their phone at 11pm. Total game changer.", who: "Head of Ops", tag: "Supplements brand" },
  { quote: "Invoices used to be a black box. Now everything is itemized, downloadable and I can pay with one click. Our finance team actually thanked us.", who: "Co-founder", tag: "Home goods brand" },
  { quote: "Returns were our biggest headache. Seeing photos, condition and where each unit went — it's the cleanest returns process we've ever had.", who: "Operations Manager", tag: "Footwear brand" },
  { quote: "Onboarding took an afternoon, not a month. We were watching real orders in the dashboard the same week we signed.", who: "CEO", tag: "Beauty brand" },
  { quote: "What sold us was the WhatsApp support group. Real answers in minutes, not a ticket number and a three-day wait.", who: "Brand Lead", tag: "Accessories brand" },
  { quote: "Same-day shipping and a dashboard that never lies to me. My accuracy questions basically disappeared.", who: "Fulfillment Lead", tag: "Pet products brand" },
  { quote: "We've scaled 4x with BoxHero and the tools scaled with us. It feels less like a vendor and more like part of our team.", who: "Founder", tag: "Subscription box brand" },
];

const STEPS = [
  { n: "1", title: "Send us your inventory", desc: "Connect your store and ship stock to our fulfillment center." },
  { n: "2", title: "We pick, pack & ship", desc: "Same-day processing with a 99.9% order accuracy rate." },
  { n: "3", title: "Watch it all in your dashboard", desc: "Track orders, inventory, invoices and returns in real time." },
  { n: "4", title: "Scale with a partner", desc: "A dedicated team and modern tools that grow with your brand." },
];

export default function PlatformShowcase() {
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") setLightbox(null); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    document.body.style.overflow = lightbox ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [lightbox]);

  return (
    <div className="bg-white text-[#101820]" data-testid="platform-showcase" style={{ fontFamily: F }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700;800;900&display=swap');
        .ps-scroll-hide{-ms-overflow-style:none;scrollbar-width:none}
        .ps-scroll-hide::-webkit-scrollbar{display:none}
      `}</style>

      {/* Sticky header */}
      <div className="sticky top-0 z-30 bg-white/95 backdrop-blur border-b border-[#E5E9EE]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-2">
          <img src={LOGO_COLOR} alt="BoxHero" className="h-7 sm:h-8 w-auto" />
          <a href={BOOK_A_CALL_URL} data-testid="header-book-call"
            className="inline-flex items-center gap-2 text-sm font-semibold text-white rounded-full px-5 py-2.5 transition-colors"
            style={{ background: "#406CB4" }}
            onMouseOver={(e) => (e.currentTarget.style.background = "#335A99")}
            onMouseOut={(e) => (e.currentTarget.style.background = "#406CB4")}>
            <PhoneCall className="w-4 h-4" /> Book a call
          </a>
        </div>
      </div>

      {/* HERO */}
      <header className="relative overflow-hidden text-white" style={{ background: "#406CB4" }}>
        <div aria-hidden className="absolute -top-24 -right-24 w-[420px] h-[420px] rounded-full" style={{ background: "rgba(249,170,37,0.20)", filter: "blur(60px)" }} />
        <div aria-hidden className="absolute inset-0 opacity-[0.08]" style={{ backgroundImage: "radial-gradient(#ffffff 1.5px, transparent 1.5px)", backgroundSize: "26px 26px" }} />
        <div className="relative max-w-6xl mx-auto px-5 sm:px-6 py-16 md:py-28">
          <p className="text-sm sm:text-base font-medium italic text-white/90">Your Brand's Fulfillment Partner</p>
          <h1 className="mt-4 font-extrabold leading-[1.02] tracking-tight text-[2.6rem] sm:text-6xl md:text-7xl max-w-4xl">
            More than a warehouse.<br />
            <span style={{ color: "#F9AA25" }}>Your command center.</span>
          </h1>
          <p className="mt-6 text-base sm:text-lg md:text-xl text-white/85 max-w-2xl leading-relaxed">
            BoxHero doesn't just store and ship your products — we give every brand a beautiful,
            self-serve dashboard to see orders, tracking, invoices, inventory and returns in real time.
            That's what sets us apart.
          </p>
          <div className="mt-9 flex flex-col sm:flex-row gap-3">
            <a href={BOOK_A_CALL_URL} data-testid="hero-book-call"
              className="inline-flex items-center justify-center gap-2 font-semibold rounded-full px-8 py-4 transition-colors shadow-lg"
              style={{ background: "#F9AA25", color: "#101820" }}
              onMouseOver={(e) => (e.currentTarget.style.background = "#E0961E")}
              onMouseOut={(e) => (e.currentTarget.style.background = "#F9AA25")}>
              Book a call. Let's talk. <ArrowRight className="w-4 h-4" />
            </a>
            <a href="#platform"
              className="inline-flex items-center justify-center gap-2 font-semibold rounded-full px-8 py-4 border-2 border-white/70 text-white hover:bg-white/10 transition-colors">
              See the dashboard
            </a>
          </div>
          <div className="mt-10 flex flex-wrap gap-x-6 gap-y-2 text-sm font-medium text-white/80">
            {["99.9% order accuracy", "Same-day shipping", "Real-time dashboard"].map((t) => (
              <span key={t} className="inline-flex items-center gap-2">
                <span className="w-5 h-5 rounded-full flex items-center justify-center" style={{ background: "#F9AA25", color: "#101820" }}>
                  <Check className="w-3 h-3" strokeWidth={3} />
                </span>{t}
              </span>
            ))}
          </div>
        </div>
      </header>

      {/* WHY CHOOSE — feature box grid (matches boxhero.com middle-of-page) */}
      <section className="py-16 md:py-24" style={{ background: "#FFFDF5" }}>
        <div className="max-w-6xl mx-auto px-5 sm:px-6">
          <div className="text-center max-w-2xl mx-auto">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-extrabold leading-tight">
              <span style={{ color: "#F9AA25" }}>Why Brands Choose </span>
              <span className="text-[#101820]">BoxHero</span>
            </h2>
            <p className="mt-4 text-[#5A6672] text-base sm:text-lg">
              A fulfillment partner and a full software platform in one. Everything your brand needs to
              stay in control — without lifting a finger.
            </p>
          </div>

          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {FEATURES.map((f) => <FeatureBox key={f.title} f={f} />)}
          </div>

          <div className="mt-12 text-center">
            <a href={BOOK_A_CALL_URL} data-testid="why-book-call"
              className="inline-flex items-center gap-2 font-semibold text-white rounded-full px-8 py-4 transition-colors"
              style={{ background: "#406CB4" }}
              onMouseOver={(e) => (e.currentTarget.style.background = "#335A99")}
              onMouseOut={(e) => (e.currentTarget.style.background = "#406CB4")}>
              Book a call. Let's talk. <ArrowRight className="w-4 h-4" />
            </a>
          </div>
        </div>
      </section>

      {/* SHOWCASE */}
      <section id="platform" className="py-16 md:py-24 bg-white">
        <div className="max-w-6xl mx-auto px-5 sm:px-6">
          <div className="text-center max-w-2xl mx-auto">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-extrabold leading-tight text-[#101820]">
              See the <span style={{ color: "#F9AA25" }}>platform</span>
            </h2>
            <p className="mt-4 text-[#5A6672] text-base sm:text-lg">
              The same screens your brand uses every day — and the tools that power the warehouse behind them.
              <span className="lg:hidden font-semibold" style={{ color: "#406CB4" }}> Swipe to browse →</span>
            </p>
          </div>
          <div className="mt-12 flex lg:grid lg:grid-cols-3 gap-5 md:gap-6 overflow-x-auto lg:overflow-visible snap-x snap-mandatory -mx-5 px-5 lg:mx-0 lg:px-0 pb-4 lg:pb-0 ps-scroll-hide">
            {SHOWCASE.map((s) => <BrowserFrame key={s.src} {...s} onOpen={() => setLightbox(s)} />)}
          </div>
        </div>
      </section>

      {/* TWO AUDIENCES */}
      <section className="py-16 md:py-24" style={{ background: "#FFFDF5" }}>
        <div className="max-w-6xl mx-auto px-5 sm:px-6">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-extrabold leading-tight text-[#101820]">
              Built to work <span style={{ color: "#F9AA25" }}>both ways</span>
            </h2>
            <p className="mt-4 text-[#5A6672] text-base sm:text-lg">
              A premium experience for your brands, and powerful operations for the team behind them.
            </p>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <AudienceCard
              accent="#406CB4"
              icon={Store}
              eyebrow="For growing brands"
              title="Total visibility, zero busywork"
              points={[
                "A live dashboard for orders, shipments and inventory",
                "Self-serve invoices and secure online payment",
                "Returns, claims and reports at your fingertips",
                "WhatsApp support — fast, human, no tickets",
              ]}
            />
            <AudienceCard
              accent="#F9AA25"
              dark
              icon={Boxes}
              eyebrow="For our operations team"
              title="Accurate, fast, accountable"
              points={[
                "Scanner-first receiving with smart bin logic",
                "Automated, usage-based Stripe billing",
                "Modern returns processing with photo capture",
                "Full audit trails from dock to dispatch",
              ]}
            />
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-6xl mx-auto px-5 sm:px-6">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-extrabold leading-tight text-[#101820]">
              What our brands <span style={{ color: "#F9AA25" }}>say</span>
            </h2>
            <p className="mt-4 text-[#5A6672] text-base sm:text-lg">
              Real feedback from brands we fulfill for. Names are kept private at our clients' request —
              but every word is theirs.
            </p>
          </div>
          <div className="columns-1 sm:columns-2 lg:columns-3 gap-6 [column-fill:_balance]">
            {TESTIMONIALS.map((t) => <QuoteCard key={`${t.who}-${t.tag}`} t={t} />)}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="py-16 md:py-24" style={{ background: "#FFFDF5" }}>
        <div className="max-w-6xl mx-auto px-5 sm:px-6">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-extrabold leading-tight text-[#101820]">
              How BoxHero <span style={{ color: "#F9AA25" }}>works</span>
            </h2>
            <p className="mt-4 text-[#5A6672] text-base sm:text-lg">
              From your first shipment to a fully managed fulfillment operation — in four simple steps.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {STEPS.map((s) => (
              <div key={s.n} className="relative rounded-2xl border border-[#E5E9EE] p-6 bg-white shadow-sm">
                <div className="w-12 h-12 rounded-full flex items-center justify-center text-xl font-extrabold text-white"
                  style={{ background: "#406CB4" }}>{s.n}</div>
                <h3 className="mt-4 text-lg font-bold text-[#101820]">{s.title}</h3>
                <p className="mt-2 text-sm text-[#5A6672] leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="px-5 sm:px-6 py-16 md:py-20 bg-white">
        <div className="max-w-6xl mx-auto rounded-[28px] text-white px-6 sm:px-10 md:px-14 py-14 md:py-16 relative overflow-hidden text-center" style={{ background: "#406CB4" }}>
          <div aria-hidden className="absolute -top-16 -right-10 w-72 h-72 rounded-full" style={{ background: "rgba(249,170,37,0.22)", filter: "blur(60px)" }} />
          <div className="relative">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-extrabold leading-tight max-w-3xl mx-auto">
              Ready to scale with <span style={{ color: "#F9AA25" }}>BoxHero?</span>
            </h2>
            <p className="mt-5 text-white/85 max-w-2xl mx-auto text-base sm:text-lg leading-relaxed">
              Let's talk about your fulfillment, delivery and returns — and show you the dashboard your brand will actually love.
            </p>
            <a href={BOOK_A_CALL_URL} data-testid="final-book-call"
              className="mt-9 inline-flex items-center gap-2 font-semibold rounded-full px-8 py-4 transition-colors"
              style={{ background: "#F9AA25", color: "#101820" }}
              onMouseOver={(e) => (e.currentTarget.style.background = "#E0961E")}
              onMouseOut={(e) => (e.currentTarget.style.background = "#F9AA25")}>
              <PhoneCall className="w-5 h-5" /> Book a call. Let's talk.
            </a>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="border-t border-[#E5E9EE]">
        <div className="max-w-6xl mx-auto px-5 sm:px-6 py-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <img src={LOGO_COLOR} alt="BoxHero" className="h-9 w-auto" />
          <p className="text-sm text-[#7A8E9B] text-center sm:text-right">
            Your Brand's Fulfillment Partner · Brooklyn, NY<br />
            <a href="mailto:3pl@boxhero.com" className="font-semibold" style={{ color: "#406CB4" }}>3pl@boxhero.com</a> · boxhero.com
          </p>
        </div>
      </footer>

      {lightbox && <Lightbox shot={lightbox} onClose={() => setLightbox(null)} />}
    </div>
  );
}

function QuoteCard({ t }) {
  return (
    <div className="mb-6 break-inside-avoid rounded-2xl bg-white border border-[#E5E9EE] p-6 shadow-sm hover:shadow-md transition-shadow" data-testid="quote-card">
      <Quote className="w-8 h-8" style={{ color: "#F9AA25" }} fill="currentColor" />
      <p className="mt-3 text-[15px] leading-relaxed text-[#34485A]">{t.quote}</p>
      <div className="mt-5 flex items-center gap-3 pt-4 border-t border-[#E5E9EE]">
        <div className="w-9 h-9 rounded-full flex items-center justify-center text-white text-xs font-bold" style={{ background: "#406CB4" }}>
          {t.who.split(" ").filter((w) => w[0] === w[0].toUpperCase()).map((w) => w[0]).join("").slice(0, 2)}
        </div>
        <div>
          <p className="text-sm font-bold text-[#101820]">{t.who}</p>
          <p className="text-xs text-[#7A8E9B]">{t.tag} · name withheld</p>
        </div>
      </div>
    </div>
  );
}

function FeatureBox({ f }) {
  const Icon = f.icon;
  return (
    <div className="group rounded-2xl bg-white border border-[#E5E9EE] p-7 shadow-sm hover:shadow-lg hover:-translate-y-1 transition-[transform,box-shadow] duration-200 text-center sm:text-left"
      data-testid="feature-box">
      <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto sm:mx-0"
        style={{ background: "rgba(64,108,180,0.10)" }}>
        <Icon className="w-8 h-8" strokeWidth={2} style={{ color: "#406CB4" }} />
      </div>
      <h3 className="mt-5 text-lg font-bold text-[#101820]">{f.title}</h3>
      <p className="mt-2 text-sm text-[#5A6672] leading-relaxed">{f.desc}</p>
    </div>
  );
}

function AudienceCard({ accent, dark, icon: Icon, eyebrow, title, points }) {
  return (
    <div className="rounded-3xl overflow-hidden border border-[#E5E9EE] bg-white shadow-sm" data-testid="audience-card">
      <div className="px-7 sm:px-9 py-7" style={{ background: accent }}>
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl flex items-center justify-center"
            style={{ background: dark ? "rgba(16,24,32,0.12)" : "rgba(255,255,255,0.18)", color: dark ? "#101820" : "#ffffff" }}>
            <Icon className="w-6 h-6" strokeWidth={2.2} />
          </div>
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.16em]" style={{ color: dark ? "rgba(16,24,32,0.65)" : "rgba(255,255,255,0.85)" }}>{eyebrow}</p>
            <h3 className="text-xl sm:text-2xl font-extrabold" style={{ color: dark ? "#101820" : "#ffffff" }}>{title}</h3>
          </div>
        </div>
      </div>
      <ul className="p-7 sm:p-9 space-y-3">
        {points.map((p) => (
          <li key={p} className="flex items-start gap-3">
            <span className="shrink-0 mt-0.5 w-5 h-5 rounded-full flex items-center justify-center" style={{ background: accent, color: dark ? "#101820" : "#ffffff" }}>
              <Check className="w-3 h-3" strokeWidth={3} />
            </span>
            <span className="text-sm text-[#34485A] leading-relaxed">{p}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function BrowserFrame({ src, label, desc, onOpen }) {
  return (
    <figure className="group snap-center shrink-0 w-[80%] sm:w-[58%] lg:w-auto rounded-xl overflow-hidden border border-[#E5E9EE] bg-white shadow-sm hover:shadow-lg hover:-translate-y-1 transition-[transform,box-shadow] duration-200">
      <div className="flex items-center gap-1.5 px-3 py-2" style={{ background: "#406CB4" }}>
        <span className="w-2.5 h-2.5 rounded-full bg-white/40" />
        <span className="w-2.5 h-2.5 rounded-full bg-white/40" />
        <span className="w-2.5 h-2.5 rounded-full" style={{ background: "#F9AA25" }} />
      </div>
      <button type="button" onClick={onOpen} data-testid="showcase-shot" className="block w-full aspect-[16/10] overflow-hidden bg-[#F0F2F1] cursor-zoom-in">
        <img src={src} alt={label} loading="lazy"
          className="w-full h-full object-cover object-top group-hover:scale-[1.03] transition-transform duration-500" />
      </button>
      <figcaption className="px-4 py-3 border-t border-[#E5E9EE]">
        <p className="text-sm font-bold text-[#101820]">{label}</p>
        <p className="text-xs text-[#7A8E9B] mt-0.5">{desc}</p>
      </figcaption>
    </figure>
  );
}

function Lightbox({ shot, onClose }) {
  return (
    <div className="fixed inset-0 z-50 backdrop-blur-sm flex items-center justify-center p-4 sm:p-8" style={{ background: "rgba(16,24,32,0.90)" }} onClick={onClose} data-testid="lightbox">
      <button onClick={onClose} className="absolute top-4 right-4 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors" aria-label="Close">
        <X className="w-5 h-5" />
      </button>
      <figure className="max-w-6xl w-full flex flex-col items-center" onClick={(e) => e.stopPropagation()}>
        <img src={shot.src} alt={shot.label} className="max-h-[80vh] w-auto max-w-full object-contain rounded-xl shadow-2xl border border-white/10 bg-white" />
        <figcaption className="text-center mt-4">
          <p className="text-white font-bold" style={{ fontFamily: F }}>{shot.label}</p>
          <p className="text-white/60 text-sm mt-0.5">{shot.desc}</p>
        </figcaption>
      </figure>
    </div>
  );
}
